package link.erpango.quip.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Card
import androidx.compose.material.Divider
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Build
import compose.icons.fontawesomeicons.solid.Qrcode
import compose.icons.fontawesomeicons.solid.StickyNote
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.icons.FontAwesomeIcons
import compose.icons.fontawesomeicons.Solid
import compose.icons.fontawesomeicons.solid.Bus
import compose.icons.fontawesomeicons.solid.Car
import compose.icons.fontawesomeicons.solid.Motorcycle
import compose.icons.fontawesomeicons.solid.QuestionCircle
import compose.icons.fontawesomeicons.solid.ShuttleVan
import compose.icons.fontawesomeicons.solid.Truck
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.entities.VehicleType
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.VehicleColors
import link.erpango.quip.theme.VehicleColors.getStringFromKey
import link.erpango.quip.ui.components.QuipGradientCard
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import quip.composeapp.generated.resources.Res
import quip.composeapp.generated.resources.note_import_bulk_entry
import quip.composeapp.generated.resources.note_import_bulk_entry_subtitle
import quip.composeapp.generated.resources.add_new_vehicle
import quip.composeapp.generated.resources.scan_qr_code
import quip.composeapp.generated.resources.color
import quip.composeapp.generated.resources.owner

/**
 * Tarjeta de vehículo mejorada con diseño moderno
 * Incluye icono, badge de año, y mejor jerarquía visual
 */
@Composable
fun VehicleCard(
    vehicleData: DVehicle,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    QuipGradientCard(
        modifier = modifier.padding(horizontal = 16.dp, vertical = 8.dp),
        vehicleColor = vehicleData.color,
        onClick = onClick
    ) {
        // Header con icono y matrícula
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                androidx.compose.material.Surface(
                    shape = androidx.compose.foundation.shape.CircleShape,
                    color = Color.White.copy(alpha = 0.5f), // Slightly transparent white for glass effect
                    modifier = Modifier.size(40.dp)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        val vehicleIcon = when (vehicleData.vehicleType) {
                            VehicleType.CAR -> FontAwesomeIcons.Solid.Car
                            VehicleType.MOTORCYCLE -> FontAwesomeIcons.Solid.Motorcycle
                            VehicleType.TRUCK -> FontAwesomeIcons.Solid.Truck
                            VehicleType.VAN -> FontAwesomeIcons.Solid.ShuttleVan
                            VehicleType.BUS -> FontAwesomeIcons.Solid.Bus
                            VehicleType.OTHER -> FontAwesomeIcons.Solid.QuestionCircle
                        }
                        androidx.compose.material.Icon(
                            imageVector = vehicleIcon,
                            contentDescription = null,
                            tint = QuipColors.colorPrimary,
                            modifier = Modifier.size(20.dp)
                        )
                    }
                }
                Spacer(modifier = Modifier.width(12.dp))
                Column {
                    Text(
                        text = vehicleData.plate,
                        style = MaterialTheme.typography.h6,
                        fontWeight = FontWeight.Bold,
                        color = Color.Black
                    )
                    Text(
                        text = "${vehicleData.brand} ${vehicleData.model}",
                        style = MaterialTheme.typography.body2,
                        color = QuipColors.colorText
                    )
                    if (vehicleData.vin.isNotBlank()) {
                        Text(
                            text = vehicleData.vin,
                            style = MaterialTheme.typography.caption,
                            color = QuipColors.colorTextSecondary
                        )
                    }
                }
            }
            
            // Badge con año
            Text(
                text = vehicleData.year.toString(),
                style = MaterialTheme.typography.body1,
                fontWeight = FontWeight.Bold,
                color = QuipColors.colorPrimary
            )
        }
        
        Spacer(modifier = Modifier.height(16.dp))
        
        // Información adicional (Owner, Color)
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
                Column {
                Text(
                    text = stringResource(Res.string.color),
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary
                )
                Text(
                    text = getStringFromKey(VehicleColors.fromId(vehicleData.color).nameResKey),
                    style = MaterialTheme.typography.body2,
                    fontWeight = FontWeight.SemiBold,
                    color = QuipColors.colorText
                )
            }
            
                Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = stringResource(Res.string.owner),
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary
                )
                Text(
                    text = vehicleData.owner,
                    style = MaterialTheme.typography.body2,
                    fontWeight = FontWeight.SemiBold,
                    color = QuipColors.colorText,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

/**
 * Lista de tarjetas de vehículos
 */
/**
 * Lista de tarjetas de vehículos
 */
@Composable
fun VehicleCardList(
    vehicles: List<DVehicle>,
    onVehicleClick: (DVehicle) -> Unit,
    onAddVehicleClick: () -> Unit,
    onScanQrClick: () -> Unit = {},
    /** "Importar desde nota" (importación masiva); null lo oculta. */
    onImportFromNoteClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    LazyColumn(
        modifier = modifier
            .fillMaxSize()
            .background(QuipColors.colorBackground),
        contentPadding = PaddingValues(vertical = 8.dp)
    ) {
        items(vehicles.size) { index ->
            VehicleCard(
                vehicleData = vehicles[index],
                onClick = { onVehicleClick(vehicles[index]) }
            )
        }
        
        item {
            AddVehicleCard(onClick = onAddVehicleClick)
        }
        
        if (onImportFromNoteClick != null) {
            item {
                ImportFromNoteCard(onClick = onImportFromNoteClick)
            }
        }

        item {
            ScanQRCard(onClick = onScanQrClick)
        }
        
        item {
             Spacer(modifier = Modifier.height(80.dp))
        }
    }
}

/**
 * Tarjeta para añadir nuevo vehículo
 */
@Composable
fun AddVehicleCard(
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .height(100.dp)
            .clickable(onClick = onClick),
        elevation = 0.dp,
        shape = RoundedCornerShape(16.dp),
        backgroundColor = Color.Transparent,
        border = androidx.compose.foundation.BorderStroke(
            width = 2.dp,
            color = QuipColors.colorTextSecondary.copy(alpha = 0.3f)
        ) // TODO: Dashed border would be nicer
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier.padding(16.dp)
        ) {
            androidx.compose.material.Icon(
                imageVector = Icons.Default.Add,
                contentDescription = "Add Vehicle",
                tint = QuipColors.colorTextSecondary,
                modifier = Modifier.size(32.dp)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = org.jetbrains.compose.resources.stringResource(Res.string.add_new_vehicle),
                style = MaterialTheme.typography.button,
                fontWeight = FontWeight.Bold,
                color = QuipColors.colorTextSecondary
            )
        }
    }
}

/**
 * Tarjeta para importar vehículos, mantenimientos y repuestos desde una nota
 */
@Composable
fun ImportFromNoteCard(
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .heightIn(min = 100.dp)
            .clickable(onClick = onClick),
        elevation = 0.dp,
        shape = RoundedCornerShape(16.dp),
        backgroundColor = Color.Transparent,
        border = androidx.compose.foundation.BorderStroke(
            width = 2.dp,
            color = QuipColors.colorPrimary.copy(alpha = 0.4f)
        )
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier.padding(16.dp)
        ) {
            androidx.compose.material.Icon(
                imageVector = FontAwesomeIcons.Solid.StickyNote,
                contentDescription = null,
                tint = QuipColors.colorPrimary,
                modifier = Modifier.size(28.dp)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = org.jetbrains.compose.resources.stringResource(Res.string.note_import_bulk_entry),
                style = MaterialTheme.typography.button,
                fontWeight = FontWeight.Bold,
                color = QuipColors.colorPrimary
            )
            Text(
                text = org.jetbrains.compose.resources.stringResource(Res.string.note_import_bulk_entry_subtitle),
                style = MaterialTheme.typography.caption,
                color = QuipColors.colorTextSecondary,
                textAlign = androidx.compose.ui.text.style.TextAlign.Center
            )
        }
    }
}

/**
 * Tarjeta para escanear código QR de transferencia
 */
@Composable
fun ScanQRCard(
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .height(100.dp)
            .clickable(onClick = onClick),
        elevation = 0.dp,
        shape = RoundedCornerShape(16.dp),
        backgroundColor = Color.Transparent,
        border = androidx.compose.foundation.BorderStroke(
            width = 2.dp,
            color = QuipColors.colorTextSecondary.copy(alpha = 0.3f)
        )
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier.padding(16.dp)
        ) {
            androidx.compose.material.Icon(
                imageVector = FontAwesomeIcons.Solid.Qrcode,
                contentDescription = "Scan QR Code",
                tint = QuipColors.colorTextSecondary,
                modifier = Modifier.size(32.dp)
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = org.jetbrains.compose.resources.stringResource(Res.string.scan_qr_code),
                style = MaterialTheme.typography.button,
                fontWeight = FontWeight.Bold,
                color = QuipColors.colorTextSecondary
            )
        }
    }
}

@Composable
@Preview
fun VehicleCardPreview() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(QuipColors.colorBackground)
            .padding(16.dp)
    ) {
        VehicleCard(
            vehicleData = DVehicle(
                vin = "1HGBH41JXMN109186",
                plate = "ABC-1234",
                brand = "Toyota",
                model = "Corolla",
                vehicleType = VehicleType.CAR,
                year = 2020,
                color = "Rojo",
                owner = "John Doe",
                engineOil = "5W-30",
                frontTireSize = "205/55R16",
                rearTireSize = "205/55R16",
                frontTirePressureBelow160 = 32.0,
                rearTirePressureBelow160 = 32.0,
                frontTirePressureAbove160 = 30.0,
                rearTirePressureAbove160 = 30.0,
                recommendedFuel = "Gasolina",
                engineDetails = "1.8L I4",
                cylinderCount = 4
            ),
            onClick = {}
        )
    }
}
